// ─────────────────────────────────────────────────────────────
// RiskSources.js - Scandinavian Mantine Version
// Gentle chromatic design with consistent styling
// ─────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  Text,
  Button,
  TextInput,
  Collapse,
  Chip,
  Group,
  Stack,
  Paper,
  Divider,
  Container,
  Title,
  ActionIcon,
  Transition,
  Loader,
} from '@mantine/core';
import {
  IconCheck,
  IconPlus,
} from '@tabler/icons-react';
import {
  
  createRiskFromSource,
  selectRiskSource,
  deselectRiskSource,
} from '../redux/actions/riskActions';
import { Navigation } from '../components/NavigationButtons';

// helper – tolerate undefined/null strings
const safeLower = (s) => (s ?? '').toLowerCase();

/* ────────────────────────────────────────────────────────── */

function SourceCard({ src, selected, onSelect }) {
  return (
    <Transition mounted transition="fade" duration={200}>
      {(styles) => (
        <Card
          style={styles}
          onClick={() => !selected && onSelect(src)}
          withBorder
          radius="md"
          p="lg"
          mb="sm"
          sx={(theme) => ({
            cursor: selected ? 'default' : 'pointer',
            border: selected 
              ? `2px solid ${theme.colors.indigo[5]}` 
              : `1px solid ${theme.colors.gray[2]}`,
            backgroundColor: selected ? theme.colors.indigo[0] : theme.white,
            transition: 'all 200ms ease',
            '&:hover': {
              transform: selected ? 'none' : 'translateY(-2px)',
              boxShadow: selected ? theme.shadows.sm : theme.shadows.md,
              borderColor: selected ? theme.colors.indigo[5] : theme.colors.gray[3],
            },
          })}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Box sx={{ flex: 1 }}>
              <Group gap="sm" mb="xs">
                <Text 
                  fw={600} 
                  size="md" 
                  c={selected ? 'indigo.8' : 'dark.7'}
                >
                  {src.name}
                </Text>
                {selected && (
                  <ActionIcon color="indigo" variant="light" size="sm">
                    <IconCheck size={16} />
                  </ActionIcon>
                )}
              </Group>
              {src.description && (
                <Text size="sm" c="dimmed" lineClamp={2} ta="left">
                  {src.description}
                </Text>
              )}
            </Box>
          </Group>
        </Card>
      )}
    </Transition>
  );
}

/* ────────────────────────────────────────────────────────── */

export default function RiskSources({
  modelId,
  assessmentUuid,
  riskArea,
  onNext,
  onBack,
}) {
  const dispatch = useDispatch();

  /* pull riskSources + status from Redux */
  const { riskSources = [] } = useSelector(
    (s) => s.risks.models[modelId]?.riskFormData?.[riskArea] || {}
  );
  
  // Skip logic now handled by RiskAreaIntro component

  /* selected sources from Redux */
  const selectedSources = useSelector(
    (s) =>
      s.risks.models[modelId]?.riskFormData?.[riskArea]?.selectedSources || []
  );
  const selectedNames = useMemo(
    () => new Set(selectedSources.map((s) => s.name)),
    [selectedSources]
  );

  /* local state */
  const [customOpen, setCustomOpen] = useState(false);
  const [customTxt, setCustomTxt] = useState('');

  /* handlers */
  const selectSource = (src) => {
    if (!selectedNames.has(src.name)) {
      dispatch(selectRiskSource(modelId, riskArea, src));
      dispatch(
        createRiskFromSource(
          modelId,
          assessmentUuid,
          riskArea,
          src.name
        )
      );
    }
  };

  const deselectSource = (src) => {
    if (selectedNames.has(src.name)) {
      dispatch(deselectRiskSource(modelId, riskArea, src.name));
    }
  };

  const addCustom = () => {
    const name = customTxt.trim();
    if (!name) return;
    // Check if source name already exists
    const existingNames = new Set([
      ...riskSources.map(s => safeLower(s?.name)),
      ...selectedSources.map(s => safeLower(s?.name))
    ]);
    if (existingNames.has(safeLower(name))) {
      return;
    }
    const srcObj = {
      name,
      type: 'text',
      value: '',
      description: 'Custom risk source',
    };
    dispatch(selectRiskSource(modelId, riskArea, srcObj));
    dispatch(
      createRiskFromSource(
        modelId,
        assessmentUuid,
        riskArea,
        name
      )
    );
    setCustomTxt('');
    setCustomOpen(false);
  };

  // const handleSkipToggle = () => {
  //   dispatch(toggleSectionNA(modelId, riskArea));
  // };

  /* wait until Redux delivers the canonical list */
  if (!riskSources.length) {
    return (
      <Container fluid px="xl" py="xl">
        <Group justify="flex-start" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  /* ───────────── render ───────────── */
  return (
    <Container fluid px="xl" py="xl">
      {/* Header */}
      <Stack gap="md" mb="xl">
        <Title order={3} c="dark.7">
          Select Risk Sources
        </Title>
        <Text size="md" c="dimmed">
          Choose the sources that apply to {' '}
          <Text component="span" fw={600} c="dark">
            {riskArea.toLowerCase()}
          </Text>{' '}
          . Click on cards to select them, or add risk sources as needed.
        </Text>
      </Stack>

      {/* Selected Sources Summary */}
      {selectedSources.length > 0 && (
        <Paper
          withBorder
          radius="md"
          p="lg"
          mb="lg"
          sx={(theme) => ({
            backgroundColor: theme.colors.indigo[0],
            borderColor: theme.colors.indigo[2],
          })}
        >
          <Text fw={600} c="indigo.8" mb="md" size="sm">
            Selected Sources ({selectedSources.length})
          </Text>
          <Group gap="xs">
            {selectedSources.map((src) => (
              <Chip
                key={src.name}
                checked
                onChange={() => deselectSource(src)}
                variant="filled"
                radius="sm"
                size="md"
                sx={(theme) => ({
                  '& .mantine-Chip-label': {
                    backgroundColor: theme.colors.indigo[5],
                    color: theme.white,
                    '&[data-checked]': {
                      backgroundColor: theme.colors.indigo[5],
                      color: theme.white,
                    },
                  },
                })}
              >
                {src.name}
              </Chip>
            ))}
          </Group>
        </Paper>
      )}

      {/* Risk Sources List */}
      <Stack gap="sm" mb="xl">
        {riskSources.map((s) => (
          <SourceCard
            key={s.name}
            src={s}
            selected={selectedNames.has(s.name)}
            onSelect={selectSource}
          />
        ))}
      </Stack>

      {/* Custom Source Adder */}
      <Stack gap="md" mb="xl">
        <Group justify="flex-start">
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => setCustomOpen(!customOpen)}
            variant="outline"
            color="indigo"
            size="md"
            w={260}
            radius="md"
            sx={(theme) => ({
              fontWeight: 600,
              borderStyle: 'dashed',
              borderWidth: 2,
              '&:hover': {
                backgroundColor: theme.colors.indigo[0],
                borderStyle: 'solid',
              }
            })}
          >
            {customOpen ? 'Cancel' : 'Add risk source'}
          </Button>
        </Group>

        <Collapse in={customOpen}>
          <Paper
            withBorder
            radius="md"
            p="lg"
            sx={(theme) => ({
              backgroundColor: theme.colors.gray[0],
              borderColor: theme.colors.gray[2],
            })}
          >
            <Text fw={600} mb="md" size="sm" c="dark.7">
              Add Risk Source
            </Text>
            <Group grow>
              <TextInput
                placeholder="Enter risk source name"
                value={customTxt}
                onChange={(e) => setCustomTxt(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCustom()}
                radius="sm"
              />
              <Button 
                onClick={addCustom}
                disabled={!customTxt.trim()}
                radius="sm"
                sx={(theme) => ({
                  backgroundColor: theme.colors.indigo[5],
                  '&:hover': {
                    backgroundColor: theme.colors.indigo[6],
                  }
                })}
              >
                Add
              </Button>
            </Group>
          </Paper>
        </Collapse>
      </Stack>

      <Divider mb="xl" />

      {/* Navigation */}
      <Navigation
        onBack={onBack}
        onNext={() => onNext(selectedSources)}
        nextLabel="Identify Root Causes"
        nextDisabled={selectedSources.length === 0}
      />
    </Container>
  );
}
